import { Injectable, signal, Signal, inject } from '@angular/core';
import { WordData } from '../services/popup.service'; // Import WordData
import {
  AudioGenerated,
  ChatMessage,
  ChatRole,
  ChatUserSettings,
  IAgentCard,
  IConversationSettings,
  IConversationSettingsDTO,
  MessageContent,
} from '../models/agent.models';
import { CONVERSATION_AI_TOKEN, AgentCardsAbstractService } from '../models/conversation-ai.class';
import { MessageProcessingService } from './message-processing.service';
import { DCConversationPromptBuilderService } from './dc-conversation-builder.service';
import { buildObjectTTSRequest } from '../components/chat-container/chat-messages-list/chat-message/chat-message.utils';
import { USER_DATA_EXCHANGE, UserDataExchangeAbstractService } from '../models/user-data-exchange';
import { MicStatusLiteral } from '@dataclouder/ngx-mic';
import { nanoid } from 'nanoid';

@Injectable({
  providedIn: 'root',
})
export class ConversationService {
  // Services
  private agentCardService = inject<AgentCardsAbstractService>(CONVERSATION_AI_TOKEN);
  private messageProcessingService = inject(MessageProcessingService);
  private conversationBuilder = inject(DCConversationPromptBuilderService);
  private userDataExchange = inject<UserDataExchangeAbstractService>(USER_DATA_EXCHANGE);
  // State Signals
  private messagesSignal = signal<ChatMessage[]>([]);
  private isThinkingSignal = signal<boolean>(false);
  private conversationSettingsSignal = signal<IConversationSettings | null>(null);
  private isDestroyedSignal = signal<boolean>(false);
  public micStatusSignal = signal<MicStatusLiteral>('ready');
  public currentAudioStatus = signal<{ message: MessageContent; completed: boolean } | null>(null);
  private wordClickedSignal = signal<WordData | null>(null); // Signal for clicked word
  // Var State
  private avatarImages = {
    userImg: 'assets/defaults/avatar_user.png',
    assistantImg: 'assets/defaults/avatar_ai.webp',
  };

  getMessagesSignal(): Signal<ChatMessage[]> {
    return this.messagesSignal;
  }

  /**
   * Notifies subscribers that a word has been clicked in a message.
   * @param wordData The data associated with the clicked word.
   */
  public notifyWordClicked(wordData: WordData): void {
    this.wordClickedSignal.set(wordData);
  }

  /**
   * Gets the signal that emits when a word is clicked.
   * @returns A signal emitting WordData or null.
   */
  public getWordClickedSignal(): Signal<WordData | null> {
    return this.wordClickedSignal;
  }

  public createNewUserMessage(): string {
    const message: ChatMessage = {
      content: '...',
      role: ChatRole.User,
    };
    const processedMessage = this.messageProcessingService.processMessage(message, this.conversationSettingsSignal(), this.avatarImages);
    this.addMessage(processedMessage);
    return processedMessage.messageId;
  }

  // Add message to conversation
  addMessage(message: ChatMessage): string {
    this.messagesSignal.update((messages) => [...messages, message]);
    console.log('Message added:', this.messagesSignal());
    return message.messageId;
  }

  // Update message by ID
  updateMessage(messageId: string, updatedMessage: Partial<ChatMessage>): boolean {
    let found = false;

    this.messagesSignal.update((messages) => {
      return messages.map((message) => {
        if (message.messageId === messageId) {
          found = true;
          // Create a new message object with the updated properties
          return { ...message, ...updatedMessage };
        }
        return message;
      });
    });

    return found;
  }

  // Get thinking state as a signal
  isThinking(): Signal<boolean> {
    return this.isThinkingSignal;
  }

  // Set destroyed state
  setDestroyed(value: boolean): void {
    this.isDestroyedSignal.set(value);
  }

  private overrideSettingsByUserSettings(conversationSettings: IConversationSettings) {
    const userSettings = this.userDataExchange.getUserChatSettings();

    if (userSettings?.model?.provider) {
      conversationSettings.model = userSettings.model;
    }
  }

  setupConversationWithAgentCard(agentCard: IAgentCard, parseDict: { [key: string]: string } = null) {
    // Set user AI avatar image
    this.avatarImages.assistantImg = agentCard?.assets?.image?.url || this.avatarImages.assistantImg;
    const conversationSettings = this.conversationBuilder.buildConversationSettings(agentCard, parseDict);

    // Here i need some rules to see when i will override and what.
    this.overrideSettingsByUserSettings(conversationSettings);

    this.conversationSettingsSignal.set(conversationSettings);
  }

  async initConversationWithSettings(conversationSettings: IConversationSettings) {
    this.overrideSettingsByUserSettings(conversationSettings);

    this.conversationSettingsSignal.set(conversationSettings);

    await this.initConversation();
  }

  private async initConversation() {
    // Set user avatar images

    this.avatarImages.userImg = this.userDataExchange.getUserDataExchange().imgUrl;

    const conversationSettings = this.conversationSettingsSignal();

    for (const i in conversationSettings.messages) {
      conversationSettings.messages[i].messageId = nanoid();
    }

    // Find first assistant message
    const firstAssistantMsg = conversationSettings?.messages.find((message) => message.role === ChatRole.Assistant);

    this.messagesSignal.set(conversationSettings?.messages || []);

    if (firstAssistantMsg) {
      // Process the first assistant message
      const processedMessage = this.messageProcessingService.processMessage(firstAssistantMsg, this.conversationSettingsSignal(), this.avatarImages);

      // Find the index of the message with the matching ID
      const messageIndex = conversationSettings.messages.findIndex((message) => message.messageId === firstAssistantMsg.messageId);

      // If found, replace the message at that index
      if (messageIndex !== -1) {
        conversationSettings.messages[messageIndex] = processedMessage;
      }
    } else if (conversationSettings?.autoStart) {
      // Auto-start conversation if configured
      await this.sendCurrentConversation();
    }
  }

  // Initialize conversation
  async initConversationWithAgentCard(agentCard: IAgentCard, parseDict: { [key: string]: string } = null): Promise<void> {
    this.setupConversationWithAgentCard(agentCard, parseDict);
    await this.initConversation();
  }

  /**
   * Sends a user message, processes it, adds it to the conversation,
   * and triggers the AI response flow.
   * @param message The initial ChatMessage object from the user.
   * @param updateId Optional ID if this message should update an existing one (e.g., transcription added).
   * @param updateId Optional ID if this message should update an existing one (e.g., transcription added).
   * @returns A promise resolving to an object containing the user message ID and the assistant message ID (or null if an error occurred).
   */
  async sendUserMessage(message: ChatMessage, updateId: string | null = null): Promise<{ userMessageId: string; assistantMessageId: string | null } | null> {
    if (this.isThinkingSignal()) {
      console.warn('AI is already thinking, message dropped.');
      return null; // Indicate message was not sent due to AI being busy
    }

    let userMessageId: string;

    // Add or update the user message
    if (updateId) {
      // Ensure the update includes the ID if not already present in message object
      const messageToUpdate = { ...message, messageId: updateId };
      const updated = this.updateMessage(updateId, messageToUpdate);
      if (!updated) {
        console.error(`Failed to update message with ID: ${updateId}`);
        // Decide how to handle this - maybe throw an error or return null?
        return null;
      }
      userMessageId = updateId;
    } else {
      // Process and add the new message
      const processedMessage = this.messageProcessingService.processMessage(message, this.conversationSettingsSignal(), this.avatarImages);
      // Ensure ID exists (processMessage should handle this, but fallback just in case)
      processedMessage.messageId = processedMessage.messageId || nanoid();
      this.addMessage(processedMessage);
      userMessageId = processedMessage.messageId;
    }

    // Set thinking state and get AI response
    this.isThinkingSignal.set(true);
    let assistantMessageId: string | null = null;
    try {
      assistantMessageId = await this.sendCurrentConversation();
    } catch (error) {
      console.error('Error sending conversation to AI:', error);
      // Handle error appropriately, maybe set a specific state or message
    } finally {
      this.isThinkingSignal.set(false);
    }

    return { userMessageId, assistantMessageId };
  }

  // Process assistant message

  // Send current conversation to AI
  async sendCurrentConversation(): Promise<string | null> {
    if (this.isDestroyedSignal()) {
      return null;
    }

    const messages = this.messagesSignal();
    const conversationSettings = this.conversationSettingsSignal();

    if (messages.length > 31) {
      // Safety limit to prevent infinite conversations
      return null;
    }

    let conversationMessages = messages;

    // Add last prompt if available
    if (conversationSettings.last_prompt) {
      conversationMessages = [...messages, { content: conversationSettings.last_prompt, role: ChatRole.System }];
    }

    // Prepare conversation DTO
    const conversation: IConversationSettingsDTO = {
      messages: conversationMessages,
      conversationType: conversationSettings.conversationType,
      textEngine: conversationSettings.textEngine,
      model: conversationSettings.model || { modelName: '', provider: '' },
    };

    this.isThinkingSignal.set(true);

    const response = await this.agentCardService.callChatCompletion(conversation);
    if (!response) {
      console.error('No message returned from AI, is your service working?');
      throw new Error('No message returned from AI');
    }

    // Process response
    const newMessage = this.messageProcessingService.processMessage(response, conversationSettings, this.avatarImages);

    // Add to messages
    this.addMessage(newMessage);

    this.isThinkingSignal.set(false);

    return newMessage.messageId;
  }

  // Reset conversation
  async resetConversation(agentCard?: IAgentCard): Promise<void> {
    // Clear messages
    this.messagesSignal.set([]);
  }

  public async getTTSFile(message: MessageContent): Promise<AudioGenerated> {
    const userChatSettings = await this.userDataExchange.getUserChatSettings();

    const ttsRequest = buildObjectTTSRequest(message, userChatSettings);
    return this.agentCardService.getTextAudioFile(ttsRequest);
  }
}
